import { API_BASE_URL, PAGE_ID } from "@/config";

export class ApiError extends Error {
  constructor(message: string, public status: number, public data?: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

export const authStorageKey = `farmer:${PAGE_ID}:session`;
export const authExpiredEvent = "farmer:auth-expired";

export function getStoredToken(): string | null {
  try {
    const session = JSON.parse(localStorage.getItem(authStorageKey) || "null") as { token?: string } | null;
    return session?.token || null;
  } catch {
    return null;
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getStoredToken();
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("Accept", "application/json");
  const response = await fetch(`${API_BASE_URL}${path}`, { ...init, headers, credentials: "include" });
  const isJson = response.headers.get("content-type")?.includes("application/json");
  const data = isJson ? await response.json().catch(() => ({})) : await response.text();
  if (!response.ok) {
    const message = typeof data === "object" && data
      ? String((data as Record<string, unknown>).message || (data as Record<string, unknown>).error || "တောင်းဆိုမှု မအောင်မြင်ပါ။")
      : `တောင်းဆိုမှု မအောင်မြင်ပါ (${response.status})`;
    if (response.status === 401 && token) {
      localStorage.removeItem(authStorageKey);
      window.dispatchEvent(new Event(authExpiredEvent));
    }
    throw new ApiError(message, response.status, data);
  }
  return data as T;
}

export function userError(cause: unknown, fallback: string): string {
  if (!(cause instanceof ApiError)) return cause instanceof Error && /[\u1000-\u109f]/.test(cause.message) ? cause.message : fallback;
  if (cause.status === 401) return "အကောင့်ဝင်ချိန် ကုန်ဆုံးသွားပါပြီ။ ပြန်လည်အကောင့်ဝင်ပါ။";
  if (cause.status === 403) return "ဤလုပ်ဆောင်ချက်ကို ပြုလုပ်ခွင့်မရှိပါ။";
  if (cause.status === 404) return "ရှာဖွေထားသည့် အချက်အလက်ကို မတွေ့ပါ။";
  if (cause.status === 429) return "တောင်းဆိုမှုများလွန်းနေပါသည်။ ခဏစောင့်ပြီး ထပ်မံကြိုးစားပါ။";
  return /[\u1000-\u109f]/.test(cause.message) ? cause.message : fallback;
}

export const jsonBody = (value: unknown): RequestInit => ({
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(value),
});
